/**
 * Silent auto-resume decision (bug 2026-08).
 *
 * When the client's tus.onError transient-error branch runs, it auto-pauses
 * the upload with `pauseReason: 'AUTO'` so the card stays visible + the
 * card+backend row stay in sync. The user shouldn't then have to *manually*
 * resume every time the connection improves — the browser should retry on
 * their behalf. This helper decides which uploads to fire a resume for on
 * a given network-recovery tick.
 *
 * Guards:
 *   - Only touches `pauseReason: 'AUTO'`. USER / WATCHDOG / STALL pauses
 *     are the user's or the sweeper's decision and stay respected.
 *   - Per-uploadId cool-down (default 30s). Prevents resume-storm during a
 *     flappy network where online/offline flip rapidly.
 *   - Max attempts (default 3). If silent resume has been tried three times
 *     without sticking, this uploadId falls through to the rate-limited
 *     popup (fix 6) so the user learns the network isn't recovering.
 *
 * @param {Array<{uploadId: string, status: string, pauseReason?: string}>} uploads
 * @param {object}  options
 * @param {number}  options.now                            wall-clock ms
 * @param {Map<string, number>} [options.lastAttemptByUploadId]  uploadId → last-attempt timestamp
 * @param {Map<string, number>} [options.attemptsByUploadId]     uploadId → cumulative attempts
 * @param {number}  [options.cooldownMs=30000]
 * @param {number}  [options.maxAttempts=3]
 * @returns {string[]} uploadIds to dispatch resumeUpload for
 */
export const decideAutoResumeUploads = (uploads, options = {}) => {
  const {
    now,
    lastAttemptByUploadId = new Map(),
    attemptsByUploadId = new Map(),
    cooldownMs = 30_000,
    maxAttempts = 3,
  } = options;
  if (!Array.isArray(uploads) || uploads.length === 0) return [];

  const out = [];
  for (const upload of uploads) {
    if (!upload || upload.status !== "PAUSED") continue;
    if (upload.pauseReason !== "AUTO") continue;
    const attempts = attemptsByUploadId.get(upload.uploadId) || 0;
    if (attempts >= maxAttempts) continue;
    const lastAt = lastAttemptByUploadId.get(upload.uploadId) || 0;
    if (now - lastAt < cooldownMs) continue;
    out.push(upload.uploadId);
  }
  return out;
};
